// BoardCorner (round 2, a board composition): the corner over the names, in TimeScale's first cell,
// its left edge on the names' left edge. Two icons (concept A, Amir 26 Oct):
// - Group: one press groups the lanes by division, or stops; the ▾ beside it, drawn as one frame with
//   it, opens Group's menu, as a right-click, the menu key, Shift+F10 or a long press on Group do. An
//   iPad has no right-click, so the ▾ stays in view. Off (no divisions yet, for someone who can't set
//   them up) it stays in view, muted, its tip and its menu saying why.
// - Filter: the pop-over of State, Department, Job type and Tag (radio groups) and the technicians.
//   With filters on it is filled and its count sits over its corner; its name says how many ("Filter,
//   2 on"), and the pop-over has Clear.
// Each icon's name shows on hover (IconButton's tip). The three are quiet small controls (option A,
// Amir 26 Oct; pages/board-corner): 26 px, no frame until on, Filter's count a word inside it ("⏷ 2"),
// 44 px to press at touch widths; with Group on, its ▾ gets an ink edge, so the two read as one.
// Reads only --corner-*.
import { useEffect, useId, useRef, useState, type KeyboardEvent, type ReactNode } from 'react';
import { IconButton } from '../IconButton/IconButton.tsx';
import { Link } from '../Link/Link.tsx';
import { Menu, type MenuAt, type MenuSection } from '../Menu/Menu.tsx';
import { Popover } from '../Popover/Popover.tsx';
import './BoardCorner.css';

/** How long a finger rests on Group before its menu opens (ms). */
export const LONG_PRESS_MS = 500;

export interface GroupMenu {
  /** Its head: "Group by division", or "No divisions yet". */
  title: string;
  /** Under it, muted: "Company default: grouped · yours: not grouped". */
  note?: string;
  sections: MenuSection[];
  onAction?: (id: string) => void;
}

export interface BoardCornerProps {
  /** Group shows (false: the company has no departments at all). */
  canGroup?: boolean;
  grouped?: boolean;
  onGroupChange?: (grouped: boolean) => void;
  /** Group can't be used now, and why: muted, the reason in its tip. */
  groupOff?: string | null;
  /** Group's tip's second line ("Right-click or ▾ for options"). */
  groupNote?: string;
  /** Group's menu; without one there is no ▾. */
  groupMenu?: GroupMenu | null;
  /** For stories: Group's menu open when it's drawn. */
  groupMenuOpen?: boolean;
  /** How many filters are on. */
  filterCount?: number;
  /** The pop-over's ChoiceGroups (State, Department, Job type, Tag). */
  filters: ReactNode;
  onClear?: () => void;
  filterOpen?: boolean;
  onFilterOpenChange?: (open: boolean) => void;
}

export function BoardCorner({
  canGroup = true,
  grouped = false,
  onGroupChange,
  groupOff,
  groupNote,
  groupMenu,
  groupMenuOpen = false,
  filterCount = 0,
  filters,
  onClear,
  filterOpen,
  onFilterOpenChange,
}: BoardCornerProps) {
  const on = filterCount > 0;
  const frame = useRef<HTMLSpanElement>(null);
  const [at, setAt] = useState<MenuAt | null>(null);
  const menuId = useId();
  const longPress = useRef<{ timer: ReturnType<typeof setTimeout> | null; fired: boolean }>({
    timer: null,
    fired: false,
  });
  const openMenu = () => {
    if (groupMenu && frame.current) setAt({ element: frame.current });
  };
  // a story's menu, open as it's first drawn
  useEffect(() => {
    if (groupMenuOpen) openMenu();
  }, [groupMenuOpen]);
  const menuKey = (e: KeyboardEvent<HTMLElement>) => {
    if (e.key !== 'ContextMenu' && !(e.shiftKey && e.key === 'F10')) return;
    e.preventDefault();
    openMenu();
  };
  const groupTip = {
    title: grouped ? 'Grouped by division' : 'Group by division',
    ...(groupOff ? { note: groupOff } : groupNote ? { note: groupNote } : {}),
  };
  return (
    <div className="fl-corner">
      {canGroup ? (
        <span
          ref={frame}
          className="fl-corner__group"
          onContextMenu={(e) => {
            if (!groupMenu) return;
            e.preventDefault();
            openMenu();
          }}
          onKeyDown={menuKey}
          // a finger held on Group opens its menu; letting go after that doesn't press it
          onPointerDown={(e) => {
            if (e.pointerType !== 'touch' || !groupMenu) return;
            longPress.current.fired = false;
            longPress.current.timer = setTimeout(() => {
              longPress.current.fired = true;
              openMenu();
            }, LONG_PRESS_MS);
          }}
          onPointerUp={() => {
            if (longPress.current.timer) clearTimeout(longPress.current.timer);
          }}
          onPointerCancel={() => {
            if (longPress.current.timer) clearTimeout(longPress.current.timer);
          }}
        >
          <IconButton
            icon="group"
            label={groupOff ? 'Group by division: none set up yet' : 'Group by division'}
            isSelected={grouped}
            off={!!groupOff}
            onChange={(g) => {
              if (longPress.current.fired) {
                longPress.current.fired = false;
                return;
              }
              onGroupChange?.(g);
            }}
            tip={groupTip}
            size="sm"
            quiet
            {...(groupMenu ? { join: 'end' as const } : {})}
          />
          {groupMenu ? (
            <IconButton
              icon="chevron-down"
              label="Grouping options"
              caret
              join="start"
              size="sm"
              quiet
              tip="Grouping options"
              menu={{ expanded: at !== null, ...(at ? { controls: menuId } : {}) }}
              onPress={() => (at ? setAt(null) : openMenu())}
            />
          ) : null}
        </span>
      ) : null}
      {groupMenu ? (
        <Menu
          id={menuId}
          at={at}
          onClose={() => setAt(null)}
          label={groupMenu.note ? `Grouping options. ${groupMenu.note}` : 'Grouping options'}
          title={groupMenu.title}
          {...(groupMenu.note ? { note: groupMenu.note } : {})}
          sections={groupMenu.sections}
          onAction={groupMenu.onAction}
        />
      ) : null}
      <Popover
        label="The filter"
        isOpen={filterOpen}
        onOpenChange={onFilterOpenChange}
        trigger={
          <IconButton
            icon="filter"
            label={on ? `Filter, ${filterCount} on` : 'Filter'}
            count={filterCount}
            tip={on ? `Filter · ${filterCount} on` : 'Filter'}
            filled={on}
            size="sm"
            quiet
          />
        }
      >
        {filters}
        {on && onClear ? (
          <div className="fl-corner__clear">
            <Link onPress={onClear}>Clear the filter</Link>
          </div>
        ) : null}
      </Popover>
    </div>
  );
}
